import { computed } from "vue";
import { get, set, cloneDeep, isEqual } from "lodash";
import { until } from "@vueuse/core"
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { api } from "/app/plugins/services";
import { usePlatform } from "/app/composables/platform";
import { useToast } from "/app/composables/toast";
import {
    useUserPreferences,
    usePlatformPreferences,
    isFetchedThisLoad,
    SAVE_PREFERENCE_KEY,
} from "/app/queries/preferences";

// The API only takes a whole slice, so each save PUTs everything under its
// scope. Saves in a scope run one at a time (mutation `scope`), and each one
// sends the cache as it stands then, optimistic values of later saves included.
const SCOPES = {
    user: {
        useQuery: () => useUserPreferences(),
        persist: (queryKey, data) => api.me.savePreferences(data),
    },
    platform: {
        useQuery: () => {
            const { currentPlatform } = usePlatform();

            return usePlatformPreferences(computed(() => currentPlatform.value?.id));
        },
        persist: (queryKey, data) => api.platform.savePreferences(queryKey.at(-1), data),
    },
};

const withValue = (result, path, value) => {
    return result && { ...result, data: set(cloneDeep(result.data), path, value) };
};

/**
 * One preference, read from the query cache. `scope` is `user` (global to the
 * signed-in account) or `platform` (the platform being viewed); `path` points
 * into that slice.
 *
 * - `model`: two-way binding; a write saves and toasts if that fails.
 * - `save(value)`: optimistic; reverts and rejects if the request fails.
 * - `ready`: the slice was fetched during this page load, so it may be
 *   written back. `save` waits for it.
 */
export const usePreference = ({ scope, path, default: defaultValue }) => {
    const { persist, useQuery } = SCOPES[scope];
    const { data, queryKey } = useQuery();
    const queryClient = useQueryClient();
    const toast = useToast();

    const ready = computed(() => isFetchedThisLoad(data.value));

    const { mutateAsync, isPending: saving } = useMutation({
        mutationKey: SAVE_PREFERENCE_KEY,
        scope: { id: `preferences:${scope}` },
        mutationFn: ({ queryKey }) => persist(queryKey, queryClient.getQueryData(queryKey)?.data ?? {}),
        onMutate: async ({ queryKey, path, value }) => {
            await queryClient.cancelQueries({ queryKey });

            const previous = get(queryClient.getQueryData(queryKey)?.data, path);

            queryClient.setQueryData(queryKey, (result) => withValue(result, path, value));

            return { previous };
        },
        onError: (error, { queryKey, path, value }, context) => {
            // A later save to the same path owns it now; leave that one be.
            queryClient.setQueryData(queryKey, (result) => {
                if (!context || !isEqual(get(result?.data, path), value)) return result;

                return withValue(result, path, context.previous);
            });
        },
        onSettled: (response, error, { queryKey }) => queryClient.invalidateQueries({ queryKey }),
    });

    const save = async (value) => {
        await until(ready).toBe(true);

        // Captured now: a save started on one platform never lands on another.
        await mutateAsync({ queryKey: queryKey.value, path, value });
    };

    const model = computed({
        get: () => get(data.value?.data, path, defaultValue),
        set: (next) => {
            save(next).catch(() => {
                toast.error('Could not save your preference. Try again.');
            });
        },
    });

    return { model, ready, saving, save };
};

/** Records the user slice refers to by id (e.g. forum channels). */
export const useUserPreferenceRelationships = () => {
    const { data } = useUserPreferences();

    return computed(() => data.value?.relationships ?? {});
};
